import ProductModal from './ProductModal';
import { useState, useEffect } from 'react';
import ProductGridItem from './ProductGridItem';
import InfinitePagination from '../../modules/InfinitePagination';
import { AxiosResponse } from 'axios';
import { ENV } from '../../constants/environments';
import { useDispatch, connect } from 'react-redux';
import PFLProductModal from './PFLProductModal';
import { ApplicationState } from '../../types/state/storeTypes';
import { ProductOwnershipResponse } from '../../types/api/OwnershipTypes';
import { useRouter } from 'next/router';
import getLicenseTypes from '../../hooks/getLicenseTypes';
import {
  handleAddProductIdToRoute,
  handleRemoveProductIdFromRoute
} from '../../helpers/addProductIdToUrl';
import LicenseConfirmationFlow from '../license-confirmation-modals/LicenseConfirmationFlow';
import { getLicenseeInfo } from '../helpers/GetLicenseInfo';
import EmailNotVerifiedDialog from '../shared/EmailNotVerifiedDialog';

function ProductGrid(props) {
  const { products, collection, ownership: _ownership, auth, user, isPfl, subscription } = props;

  const [visibleProducts, setVisibleProducts] = useState(products);
  const [selectedDownload, setSelectedDownload] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [endReached, setEndReached] = useState(products.length == 50 ? false : true);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModal, setIsModal] = useState(false);
  const [ownership, setOwnership] = useState<{ [key: number]: ProductOwnershipResponse }>(_ownership);
  const apiUrl: string = `${ENV.api.baseURL}`;
  const dispatch = useDispatch();
  const router = useRouter();
  const [isConfirmLicenseFlowOpen, setIsConfirmLicenseFlowOpen] = useState(false);
  const [isEmailVerificationModalOpen, setEmailVerificationModalOpen] = useState(false);

  const handleLicenseFlowClose = () => {
    setIsConfirmLicenseFlowOpen(false);
  };

  const { licenseTypes } = getLicenseTypes();

  const isLicenseeProfileComplete = !!user?.team?.licensee_profile?.completed;

  const licenseeInfo = getLicenseeInfo(isLicenseeProfileComplete, user);

  useEffect(() => {
    setOwnership(_ownership);
  }, [_ownership]);

  const handleOwnershipUpdateFromChildren = (ownershipUpdate: ProductOwnershipResponse) => {
    let newOwnershipState: { [key: number]: ProductOwnershipResponse };
    !!ownership ?
      newOwnershipState = { ...ownership, [ownershipUpdate.id]: ownershipUpdate } :
      newOwnershipState = { [ownershipUpdate.id]: ownershipUpdate };
    setOwnership(newOwnershipState);
  }

  const nextProductObj = () => {
    const current = visibleProducts.findIndex((p) => p.id == selectedProduct.id);
    return visibleProducts[current + 1];
  };
  const previousProductObj = () => {
    const current = products.findIndex((p) => p.id == selectedProduct.id);
    return visibleProducts[current - 1];
  };

  useEffect(() => {
    // PFL has no prices, product modal can be shown right away
    if (collection.library?.id == 2) {
      setSelectedProduct(props.selectedProduct);
      return;
    }

    // If authenticated === undefined, let's wait till the app
    // knows whether the user is logged in or not -
    // need to get prices with auth token for logged in users
    if (auth.authenticated === undefined) return;

    // App knows explicitly whether the user is logged in or not,
    // it is now safe to display the product modal
    setSelectedProduct(props.selectedProduct);
  }, [auth.authenticated]);

  const getProducts = async (increment: boolean = false): Promise<void> => {
    if (endReached) return;
    if (loading) return;
    setLoading(true);
    dispatch({
      type: 'GET_AUTHED_PRODUCT_ELEMENTS',
      api: {
        method: 'GET',
        url: `${apiUrl}/collections/${collection.id}/products?page=${currentPage + 1}`,
        onError: (res) => {
          setEndReached(true);
          setLoading(false);
          return;
        },
        onSuccess: (res: AxiosResponse) => {
          setLoading(false);
          if (!res?.data || res?.data?.length == 0) {
            setEndReached(true);
            return [];
          }
          setCurrentPage(currentPage + 1);
          setVisibleProducts([...visibleProducts, ...res?.data]);
        }
      }
    });
  };

  function setProductIdRouteParam(productId: string) {
    const newPath = !!productId
      ? handleAddProductIdToRoute(router.asPath, productId)
      : handleRemoveProductIdFromRoute(router.asPath, collection);
    const { id, slug, ...cleanedUpQuery } = router.query;
    router.replace(
      {
        pathname: newPath,
        query: cleanedUpQuery,
      },
      undefined,
      { shallow: true }
    );
  }

  function selectProduct(product: any) {
    setSelectedProduct(product);
    setProductIdRouteParam(product?.id || '');
  }

  return (
    <>
      <div className="grid w-full grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-x-4 gap-y-6">
        {visibleProducts.map((p) => (
          <a key={p.id} className="cursor-pointer">
            <ProductGridItem
              product={p}
              collection={collection}
              ownership={ownership && ownership[p.id]}
              selectedDownload={selectedDownload}
              setSelectedDownload={setSelectedDownload}
              setSelectedProduct={(product) => selectProduct(product)}
              isModal={isModal}
              setIsModal={setIsModal}
              setIsConfirmLicenseFlowOpen={setIsConfirmLicenseFlowOpen}
              isLicenseeProfileComplete={isLicenseeProfileComplete}
              hasLegitimateLicense={ownership && ownership[p.id]?.has_legitimate_license}
              currentLicenseeProfile={subscription?.license.type}
              isPfl={isPfl}
              setEmailVerificationModalOpen={setEmailVerificationModalOpen}
              onProductPurchased={handleOwnershipUpdateFromChildren}
              user={user}
              licenseTypes={licenseTypes}
            />
          </a>
        ))}

        {!endReached && (
          <InfinitePagination
            totalPages={999}
            currentPage={currentPage}
            loadHref="/products/page/[page]"
            loadAs="/products/page/$"
            getContent={getProducts}
          />
        )}
      </div>

      <LicenseConfirmationFlow open={isConfirmLicenseFlowOpen} onClose={handleLicenseFlowClose} isUpdate={false} />

      <EmailNotVerifiedDialog
        isOpen={isEmailVerificationModalOpen}
        setIsOpen={() => setEmailVerificationModalOpen(false)}
      />

      {selectedProduct && (
        <>
          {collection.library.id != 2 && (
            <ProductModal
              collection={collection}
              showArrows={true}
              nextProduct={nextProductObj()}
              previousProduct={previousProductObj()}
              product={selectedProduct}
              setSelectedProduct={(product) => selectProduct(product)}
              closeCallback={() => selectProduct(null)}
              ownership={ownership && ownership[selectedProduct.id]}
              onOwnershipDataLoaded={handleOwnershipUpdateFromChildren}
              isModal={isModal}
              setIsModal={setIsModal}
            />
          )}
          {collection.library.id == 2 && (
            <PFLProductModal
              collection={collection}
              showArrows={true}
              nextProduct={nextProductObj()}
              previousProduct={previousProductObj()}
              product={selectedProduct}
              setSelectedProduct={(product) => selectProduct(product)}
              closeCallback={() => selectProduct(null)}
              ownership={ownership && ownership[selectedProduct.id]}
              isModal={isModal}
              setIsModal={setIsModal}
            />
          )}
        </>
      )}
    </>
  );
}

const mapDispatchToProps = (state: ApplicationState) => ({
  auth: state.auth,
  user: state.auth.user,
  subscription: state.auth.subscription,
});

export default connect(mapDispatchToProps)(ProductGrid);
